import { useCallback, useEffect, useState } from 'react';
import { useAction } from '@/lib/api-hooks';
import toast from 'react-hot-toast';
import { Plus, RefreshCw } from 'lucide-react';
import { backendApi } from '@/lib/backend';
import { useModuleAccess } from '@/lib/useModuleAccess';
import {
  getTrunkStatusMeta,
  isTrunkEnabled,
  trunkToneToBadgeVariant,
} from '@/lib/yeastar/trunk-status';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Loader } from '@/components/ui/Loader';
import { ProvisioningTable } from './ProvisioningTable';

type TrunkDid = {
  did: string;
  did_name?: string;
};

type Trunk = {
  id: number;
  name: string;
  status: number;
  type?: string;
  host_port?: string;
  def_outbound_cid?: string;
  dids?: TrunkDid[];
};

function formatTrunkDids(trunk: Trunk): string {
  const fromList = (trunk.dids || [])
    .map((d) => d.did?.trim())
    .filter(Boolean) as string[];
  if (fromList.length) return fromList.join(', ');
  const fallback = trunk.def_outbound_cid?.trim();
  return fallback || '—';
}

interface TrunksTabProps {
  customerId: string;
}

export function TrunksTab({ customerId }: TrunksTabProps) {
  const { canManage } = useModuleAccess('trunks');

  const trunksList = useAction(backendApi.provisioning.trunksList);
  const trunksCreate = useAction(backendApi.provisioning.trunksCreate);
  const trunksSetEnabled = useAction(backendApi.provisioning.trunksSetEnabled);

  const [trunks, setTrunks] = useState<Trunk[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '',
    hostname: '',
    domain: '',
    username: '',
    password: '',
    authName: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await trunksList({ customerId, source: 'ui' });
      setTrunks(rows);
    } catch (error) {
      setTrunks([]);
      toast.error(error instanceof Error ? error.message : 'Failed to load trunks');
    } finally {
      setLoading(false);
    }
  }, [customerId, trunksList]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    try {
      await trunksCreate({ customerId, source: 'ui', ...form, enable: 0 });
      toast.success('Trunk created');
      setShowForm(false);
      setForm({ name: '', hostname: '', domain: '', username: '', password: '', authName: '' });
      void load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Create failed');
    }
  }

  async function toggleEnabled(trunk: Trunk) {
    const enabled = trunk.status === 44;
    try {
      await trunksSetEnabled({
        customerId,
        source: 'ui',
        id: trunk.id,
        name: trunk.name,
        enabled,
      });
      toast.success(`${enabled ? 'Enabled' : 'Disabled'} trunk ${trunk.name}`);
      void load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Update failed');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Trunks</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{trunks.length} SIP trunks</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => void load()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          {canManage && (
            <Button size="sm" onClick={() => setShowForm(!showForm)}>
              <Plus className="h-4 w-4 mr-1" />
              Create trunk
            </Button>
          )}
        </div>
      </div>

      {canManage && showForm && (
        <Card animate={false}>
          <form onSubmit={handleCreate} className="grid gap-4 md:grid-cols-2">
            {(['name', 'hostname', 'domain', 'username', 'password', 'authName'] as const).map(
              (field) => (
                <Input
                  key={field}
                  label={field === 'authName' ? 'Auth name' : field.charAt(0).toUpperCase() + field.slice(1)}
                  type={field === 'password' ? 'password' : 'text'}
                  value={form[field]}
                  onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                  required
                />
              )
            )}
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit">Create</Button>
              <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card animate={false} className="p-0 overflow-hidden">
        {loading ? (
          <Loader text="Loading trunks..." />
        ) : (
          <ProvisioningTable
            columns={[
              { key: 'name', label: 'Name' },
              { key: 'type', label: 'Type' },
              { key: 'host', label: 'Host' },
              { key: 'did', label: 'DID' },
              { key: 'status', label: 'Status' },
              { key: 'actions', label: '' },
            ]}
            rows={trunks.map((t) => {
              const statusMeta = getTrunkStatusMeta(t.status);
              const enabled = isTrunkEnabled(t.status);
              const didLabel = formatTrunkDids(t);
              const didTitle =
                (t.dids || [])
                  .map((d) => (d.did_name ? `${d.did} (${d.did_name})` : d.did))
                  .filter(Boolean)
                  .join('\n') || undefined;
              return {
                name: t.name,
                type: t.type ?? '—',
                host: t.host_port ?? '—',
                did: (
                  <span
                    className={`font-mono text-sm tabular-nums ${
                      didLabel === '—' ? 'text-gray-400 italic' : 'text-gray-800 dark:text-gray-100'
                    }`}
                    title={didTitle}
                  >
                    {didLabel}
                  </span>
                ),
                status: (
                  <Badge variant={trunkToneToBadgeVariant(statusMeta.tone)}>{statusMeta.label}</Badge>
                ),
                actions: canManage ? (
                  <Button size="sm" variant="secondary" onClick={() => void toggleEnabled(t)}>
                    {enabled ? 'Disable' : 'Enable'}
                  </Button>
                ) : (
                  '—'
                ),
              };
            })}
            emptyMessage="No trunks configured."
          />
        )}
      </Card>
    </div>
  );
}